/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client'

// Deep import, not the `@aglyn/aglyn` barrel (AGL-2170): the barrel pulls
// shared-data-enums -> firebase-auth into every consumer's module graph, which
// breaks specs that mock firebase wholesale (AuthErrorCodes reads undefined).
// One brand string is not worth that edge.
import { PLATFORM_BRAND_NAME } from '@aglyn/aglyn/app-utils/platform-brand'
import { trackEventBeforeNavigation } from '@aglyn/aglyn/app-utils/analytics-events'
import type { AuthResultError } from '@aglyn/shared-data-enums'
import {
  FIELD_SCHEMA_EMAIL,
  FIELD_SCHEMA_PASSWORD,
} from '@aglyn/shared-data-forms'
import { AppLink, useLoading } from '@aglyn/shared-ui-jsx'
import { useContinueHref } from '@aglyn/shared-util-next'
import type { FormSchema } from '@aglyn/shared-ui-jsx-forms'
import { FormRenderer, simpleComponentMapper } from '@aglyn/shared-ui-jsx-forms'
import {
  mdiFingerprint,
  mdiGoogle,
  mdiShieldKeyOutline,
} from '@aglyn/shared-data-mdi'
import { MdiIcon } from '@aglyn/shared-ui-jsx'
import { LoadingTextComponent } from '@aglyn/shared-ui-jsx/components/loading-text.component'
import {
  Button,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material'
import {
  browserLocalPersistence,
  GoogleAuthProvider,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type UserCredential,
} from 'firebase/auth'
import { useCallback, useState } from 'react'
import { useAuth, useSigninCheck } from '@aglyn/tenant-feature-instance'
import AuthErrorAlertComponent from '../../../components/auth-error-alert.component'
import AuthFormTemplateComponent from '../../../components/auth-form-template.component'
import AuthFormComponent from '../../../components/auth-form.component'
import { AuthLegalNotice } from '../../../components/auth-legal-consent.component'
import AuthenticatingLayout from '../../../components/layouts/authenticating.layout'
import useDelegateWorkspaceSignIn from '../../../hooks/use-delegate-workspace-signin'
import useGoogleRedirectResult from '../../../hooks/use-google-redirect-result'
import { rememberAccountAcquisition } from '../../../utils/account-acquisition'
import { authSignInHost } from '../../../utils/auth-delegation'
import { markConsentBounce } from '../../../utils/consent-bounce'
import { isNewAccount, sendToConsentGate } from '../../../utils/legal-consent'
import {
  markInteractiveSignIn,
  markInteractiveSignOut,
} from '../../../utils/interactive-signin'
import isMobileBrowser from '../../../utils/is-mobile-browser'
import { createGoogleOAuthProvider } from '../../../utils/oauth-providers'
import { aimAuthAtPool } from '../../../utils/pooled-custom-token'
import {
  describePasskeySignInFailure,
  signInWithPasskey,
  usePasskeysSupported,
} from '../../../utils/passkeys'
import guardPopupLoading from '../../../utils/popup-loading-guard'

// Built through the shared factory so the account chooser is forced on
// (AGL-1415) — a bare provider signs straight in as whatever account the
// device already holds, which on mobile is never a choice at all.
const googleOAuthProvider = createGoogleOAuthProvider()

const formSchema: FormSchema = {
  fields: [FIELD_SCHEMA_EMAIL, FIELD_SCHEMA_PASSWORD],
}

/**
 * Sign in with the typed address, falling back to the account's primary when
 * the typed one is a CONFIRMED secondary (AGL-2486).
 *
 * Firebase Auth knows one email per user, so an account whose record says
 * `ada@personal.test` cannot be signed into as `ada@work.test` however
 * thoroughly that address is confirmed. `/api/auth/resolve-identifier`
 * translates; this retries once with what it returns.
 *
 * ## Order matters, and it is this way round on purpose
 *
 * The direct attempt goes FIRST and the resolver only runs after it fails.
 * Resolving up front would put a lookup on the critical path of every single
 * sign-in in the product — slower for everyone, and an outage in the resolver
 * would become an outage in sign-in. This way the common case never touches
 * it, and a resolver that is down costs only the alias convenience.
 *
 * The retry is attempted on the credential errors alone. Anything else — a
 * disabled account, too many attempts, a network failure — is rethrown
 * untouched, so a second attempt never turns one refusal into two and never
 * doubles a rate-limit count against the user.
 */
async function signInWithAnyConfirmedAddress(
  firebaseAuth: Parameters<typeof signInWithEmailAndPassword>[0],
  typed: string,
  password: string,
) {
  try {
    return await signInWithEmailAndPassword(firebaseAuth, typed, password)
  } catch (error: any) {
    const code = error?.code as string | undefined
    // `auth/invalid-credential` is what a modern Firebase project returns for
    // BOTH a wrong password and an unknown address — email enumeration
    // protection collapses them on purpose — so it has to be one of the
    // codes that earns a retry, or the alias path would never run at all on
    // a project with that setting on.
    const retryable =
      code === 'auth/user-not-found' ||
      code === 'auth/invalid-credential' ||
      code === 'auth/invalid-email'
    if (!retryable) throw error
    let resolved: string | null = null
    try {
      const response = await fetch('/api/auth/resolve-identifier', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ identifier: typed }),
      })
      const data = await response.json().catch(() => ({}))
      resolved = typeof data?.email === 'string' ? data.email : null
    } catch {
      // Never let this lookup be the reason a sign-in fails.
    }
    // The route echoes the input when it knows nothing, so this is also the
    // "nothing to retry" test.
    if (!resolved || resolved.toLowerCase() === String(typed).trim().toLowerCase()) {
      throw error
    }
    return signInWithEmailAndPassword(firebaseAuth, resolved, password)
  }
}

function SignIn() {
  const { queueLoading, loading } = useLoading()
  const firebaseAuth = useAuth()
  const [error, setError] = useState<AuthResultError>(null)
  // Org workspace subdomains can't run OAuth — hand sign-in to the auth
  // host and skip the local form/redirect-result entirely (AGL-465).
  const delegation = useDelegateWorkspaceSignIn('signin')
  /**
   * "Sign in with Google" can create an account (AGL-1497). When it does, the
   * person in front of us has never been shown the Terms — this page only
   * carries the passive notice, because everyone reaching it was assumed to
   * have a contract already.
   *
   * So the new account is stood back down and the person is sent to /signup,
   * where the clickwrap gate is. Signing out rather than deleting: the Firebase
   * account is empty and harmless, it holds no session cookie and no org, and
   * their next click — Google on /signup — signs straight back into it and
   * records the acceptance properly. Deleting the account instead would be a
   * destructive answer to what is really a routing question.
   */
  const rejectUnconsentedNewAccount = useCallback(
    async (credential: UserCredential): Promise<boolean> => {
      if (!isNewAccount(credential)) return false
      // This IS the account's creation (AGL-3355), so it records where the
      // visit came from — before the stand-down, while the user can still
      // mint a token. The server measures its window from the auth record's
      // creation time, so a person who takes longer than that to consent on
      // /signup would otherwise be recorded by nobody: /signup's call finds
      // an account too old to be new, and one who never comes back leaves an
      // account with no record at all.
      await rememberAccountAcquisition(credential.user)
      // /signup cannot tell this account is new once it comes back — Firebase
      // has known it since this click — so this page says so, for this uid
      // (AGL-3424). That is what gets it the workspace a sign-up lands in.
      markConsentBounce(credential.user.uid)
      markInteractiveSignOut()
      await signOut(firebaseAuth).catch((error) => {
        console.error('could not stand down unconsented new account', error)
      })
      sendToConsentGate()
      return true
    },
    [firebaseAuth],
  )
  // Mobile browsers sign in via redirect (AGL-462); this completes the
  // round-trip when Google sends the user back here — and on mobile that is
  // the ONLY place the new-account bounce above can happen, because the page
  // that started the flow is gone by then.
  useGoogleRedirectResult(
    'login',
    setError,
    delegation === 'off',
    rejectUnconsentedNewAccount,
  )
  // Once auth succeeds there's a short window before the layout redirects to
  // the dashboard/continue URL; show the loading splash instead of flashing
  // the sign-in form back at the user (AGL-476).
  const { data: signInCheckResult } = useSigninCheck()
  const signedIn = signInCheckResult?.signedIn === true
  // Effect-gated so the server render (no WebAuthn) never mismatches.
  const passkeySupported = usePasskeysSupported()
  // The SSO button is a LINK to another page, and a link built from a bare
  // string drops the query it was standing on (AGL-2486). A signed-out user
  // deep-linked here arrives at `/signin?continue=…`; the password and
  // Google paths never leave this URL so they keep it, and `/sso` did not,
  // so an enterprise user authenticated successfully and landed on the
  // dashboard instead of the page they asked for.
  const ssoHref = useContinueHref('/sso')

  const handleSignIn = useCallback(
    async (values?: any) => {
      if (loading) return
      if (error) setError(null)
      const dequeueLoading = queueLoading()
      // Popup flows can wedge the overlay if the popup handle is severed
      // and the SDK never rejects — see guardPopupLoading (AGL-459).
      const releaseGuard = values
        ? undefined
        : guardPopupLoading(dequeueLoading)
      // Flag the interactive sign-in BEFORE it starts so it survives the
      // mobile redirect round-trip; the session hook mints the shared
      // cookie on return instead of validating a stale one (AGL-463).
      markInteractiveSignIn()
      // Both doors below are PROJECT-pool credentials, and `auth.tenantId` is
      // sticky instance state that `/sso` sets and nothing clears (AGL-1993).
      // The `← Back to sign in` link on `/sso` is a client-side `<Link>`, so an
      // abandoned SSO attempt hands this page an instance still aimed at the
      // GCIP tenant — and a password sign-in there fails for a user who exists.
      aimAuthAtPool(firebaseAuth, null)
      await setPersistence(firebaseAuth, browserLocalPersistence)
        .then(() => {
          if (values) {
            return signInWithAnyConfirmedAddress(
              firebaseAuth,
              values[FIELD_SCHEMA_EMAIL.name],
              values[FIELD_SCHEMA_PASSWORD.name],
            )
          }
          // Mobile popups become tabs whose result never reaches the SDK
          // (AGL-462) — the redirect flow is the only reliable path there.
          // The overlay stays queued until the browser navigates away.
          return isMobileBrowser()
            ? signInWithRedirect(firebaseAuth, googleOAuthProvider)
            : signInWithPopup(firebaseAuth, googleOAuthProvider)
        })
        .then(async (user) => {
          // Before the analytics event: this was never a login (AGL-1497).
          if (await rejectUnconsentedNewAccount(user)) return
          // A literal per door, not `user.providerId` — the Identity Toolkit
          // password response carries no provider, so this reported
          // `method: null` for every email/password sign-in (AGL-1561).
          // Reaching this `.then` means desktop; mobile resolves through
          // `useGoogleRedirectResult` and reports `google_redirect`.
          await trackEventBeforeNavigation('login', {
            method: values ? 'password' : 'google_popup',
          })
        })
        .catch((error) => {
          console.error(error)
          setError({
            ...error,
            credential: GoogleAuthProvider.credentialFromError(error),
          })
        })
        .finally(() => {
          releaseGuard?.()
          dequeueLoading()
        })
    },
    [error, firebaseAuth, loading, queueLoading, rejectUnconsentedNewAccount],
  )

  const handleFormSubmit = useCallback(
    async (values) => {
      await handleSignIn(values)
    },
    [handleSignIn],
  )
  const handleGoogleButtonClick = useCallback(async () => {
    await handleSignIn()
  }, [handleSignIn])

  // Passkey sign-in (AGL-662): the WebAuthn assertion is verified server-side
  // and bridged into Firebase via signInWithCustomToken; from there the
  // session machinery is the same as every other interactive sign-in.
  const handlePasskeyClick = useCallback(async () => {
    if (loading) return
    if (error) setError(null)
    const dequeueLoading = queueLoading()
    try {
      await signInWithPasskey(firebaseAuth)
      await trackEventBeforeNavigation('login', { method: 'passkey' })
    } catch (caught) {
      // Every outcome speaks now (AGL-1417). This used to swallow
      // NotAllowedError and AbortError as "the user cancelled" — but WebAuthn
      // also returns NotAllowedError when NO credential exists for the RP ID,
      // so the commonest case, a person who never registered a passkey, got a
      // spinner and then nothing.
      console.error(caught)
      setError(describePasskeySignInFailure(caught) as AuthResultError)
    } finally {
      dequeueLoading()
    }
  }, [error, firebaseAuth, loading, queueLoading])

  if (delegation === 'redirecting') {
    // Bouncing to the auth host (AGL-465) — no local form or OAuth here.
    return (
      <AuthFormComponent
        headingTop={'Redirecting'}
        headingBottom={'Taking you to sign in'}
        headingBottomProps={{
          sx: { pb: 4 },
          component: LoadingTextComponent,
        }}
        headingAfter={<CircularProgress color="primary" />}
      />
    )
  }
  if (signedIn) {
    // Authenticated — the layout is about to route to the dashboard/continue
    // URL. Hold the loading screen so the form doesn't flash back (AGL-476).
    return (
      <AuthFormComponent
        headingTop={'Signing in'}
        headingBottom={'One moment'}
        headingBottomProps={{
          sx: { pb: 4 },
          component: LoadingTextComponent,
        }}
        headingAfter={<CircularProgress color="primary" />}
      />
    )
  }
  if (delegation === 'stopped') {
    // Delegation kept coming back session-less (AGL-467) — surface an escape
    // instead of an endless spinner.
    return (
      <AuthFormComponent
        headingTop={'Sign-in didn’t complete'}
        headingBottom={'We couldn’t establish your session on this workspace.'}
        paperAfter={
          <Typography component="div" variant="body2">
            <Link href={`https://${authSignInHost()}/signin`}>
              {'Sign in again'}
            </Link>
          </Typography>
        }
      />
    )
  }

  return (
    <AuthFormComponent
      paperTop={
        <Typography
          component="div"
          variant="body2"
          sx={{
            alignSelf: 'flex-end',
          }}
        >
          <AppLink href="/signup">{'Create account'}</AppLink>
        </Typography>
      }
      headingTop={'Sign in'}
      headingBottom={`Use your ${PLATFORM_BRAND_NAME} account`}
      paperAfter={
        <Typography component="div" variant="body2">
          {'Having trouble logging in? '}
          <AppLink href="/account-recovery">{'Account recovery'}</AppLink>
        </Typography>
      }
    >
      <FormRenderer
        FormTemplate={AuthFormTemplateComponent}
        componentMapper={simpleComponentMapper}
        onSubmit={handleFormSubmit}
        schema={formSchema}
        subscription={{ values: true }}
        clearOnUnmount
      />
      <AuthErrorAlertComponent error={error} sx={{ mt: 2, mb: 1 }} />
      <Divider flexItem variant="middle" sx={{ my: 3 }}>
        {'Or sign in with'}
      </Divider>
      <Stack
        direction="column"
        spacing={1}
        sx={{
          justifyContent: 'center',
          alignItems: 'stretch',
          paddingBottom: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<MdiIcon path={mdiGoogle.path} />}
          onClick={handleGoogleButtonClick}
        >
          {'Google'}
        </Button>
        <Button
          variant="outlined"
          component={AppLink}
          href={ssoHref}
          startIcon={<MdiIcon path={mdiShieldKeyOutline.path} />}
        >
          {'Single sign-on (SSO)'}
        </Button>
        {passkeySupported ? (
          <Button
            variant="outlined"
            startIcon={<MdiIcon path={mdiFingerprint.path} />}
            onClick={handlePasskeyClick}
          >
            {'Passkey'}
          </Button>
        ) : null}
      </Stack>
      <AuthLegalNotice />
    </AuthFormComponent>
  )
}
SignIn.displayName = 'Page:SignIn'

export default SignIn
